import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { PublicLayout } from './BookPage';
import { Button } from '../components/ui/button';
import { GraduationCap, CalendarDays, MapPin, Users, CheckCircle2 } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function MasterclassPage() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [mine, setMine] = useState({});
  const navigate = useNavigate();
  const { t, dateLocale } = useI18n();

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(dateLocale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const load = () => {
    api.get('/masterclasses').then((r) => {
      setList(r.data.masterclasses);
      if (user) {
        r.data.masterclasses.forEach((mc) => {
          api.get(`/masterclasses/${mc.masterclass_id}/my`)
            .then((m) => setMine((prev) => ({ ...prev, [mc.masterclass_id]: m.data.registered })))
            .catch(() => {});
        });
      }
    }).catch(() => setList([]));
  };
  useEffect(() => { load(); }, [user?.user_id]);

  useEffect(() => {
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', "Rendez-vous OCQJ ! — On croit qu'on joue !");
    setMeta('og:image', `${window.location.origin}/images/flyer_masterclass.png`);
    setMeta('og:type', 'website');
  }, []);

  const register = async (mc) => {
    if (!user) {
      navigate('/connexion');
      return;
    }
    try {
      const { data } = await api.post(`/masterclasses/${mc.masterclass_id}/register`);
      if (data.status === 'already') toast.info(t('masterclass.toastAlready'));
      else toast.success(t('masterclass.toastConfirmed'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  return (
    <PublicLayout testid="masterclass-page" pageKey="page_masterclass">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('masterclass.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3">
          {t('masterclass.title')}
        </h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6 max-w-2xl">
          {t('masterclass.intro')}
        </p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }} className="mt-10">
        <img src="/images/flyer_masterclass.png"
          alt="Flyer officiel des masterclass On croit qu'on joue ! — Apprendre, partager, transmettre. Les 4 actes du livre et les contenus du programme."
          data-testid="masterclass-flyer" loading="lazy"
          className="w-full max-w-2xl mx-auto rounded-3xl ring-1 ring-or-400/30 shadow-2xl" />
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {(list || []).map((mc, i) => {
          const full = mc.places_left <= 0;
          const isMine = mine[mc.masterclass_id];
          return (
            <motion.div key={mc.masterclass_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: i * 0.08 }}
              className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 hover:border-or-400/40 transition-all"
              data-testid={`masterclass-card-${mc.masterclass_id}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center shrink-0">
                  <GraduationCap className="w-6 h-6 text-or-400" strokeWidth={2.5} />
                </span>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full
                  ${full ? 'bg-rose-400/20 text-rose-300' : 'bg-green-400/20 text-green-300'}`}
                  data-testid={`masterclass-places-${mc.masterclass_id}`}>
                  {full ? t('masterclass.full')
                    : t(mc.places_left > 1 ? 'masterclass.placeMany' : 'masterclass.placeOne', { n: mc.places_left })}
                </span>
              </div>
              <h2 className="text-2xl font-semibold text-creme mt-4">{mc.title}</h2>
              <div className="flex flex-wrap gap-4 mt-3 text-sm font-bold text-brume">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-or-400" /> {formatDate(mc.date)}</span>
                {mc.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-or-400" /> {mc.location}</span>}
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-or-400" /> {t('masterclass.registered', { count: mc.registered_count, capacity: mc.capacity })}</span>
              </div>
              {mc.description && <p className="text-base text-brume leading-relaxed mt-4">{mc.description}</p>}
              {isMine ? (
                <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-green-300 bg-green-400/15 px-5 py-2.5 rounded-full"
                  data-testid={`masterclass-registered-${mc.masterclass_id}`}>
                  <CheckCircle2 className="w-4 h-4" /> {t('masterclass.alreadyRegistered')}
                </p>
              ) : (
                <Button onClick={() => register(mc)} disabled={full} data-testid={`masterclass-register-${mc.masterclass_id}`}
                  className="mt-6 h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8 disabled:opacity-50">
                  {full ? t('masterclass.full') : user ? t('masterclass.register') : t('masterclass.loginToRegister')}
                </Button>
              )}
            </motion.div>
          );
        })}
        {list && list.length === 0 && (
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-10 text-center md:col-span-2" data-testid="masterclass-empty">
            <p className="font-bold text-lg text-creme">{t('masterclass.emptyTitle')}</p>
            <p className="text-sm text-brume mt-2">{t('masterclass.emptyText')}</p>
          </div>
        )}
        {!list && <p className="text-brume">{t('common.loading')}</p>}
      </div>
    </PublicLayout>
  );
}
